import { toast } from "react-toastify";
import React from "react";
import { obterStorage } from "./secureStorage";

const PERMISSIONS = {
  CONSULTAR: "CONSULTAR_PERMITIR",
  INSERIR: "INSERIR",
  ALTERAR: "ALTERAR",
  EXCLUIR: "EXCLUIR",
};

function toastError(toastId, mensagem) {
  if (toast.isActive(toastId)) {
    return;
  }

  toast.error(mensagem, { toastId });
}

export function usePermissoesLocalStorage() {
  const sessao = obterStorage("sessao") || {};
  const da = obterStorage("_da") || {};

  const usuarioPerfil =
    sessao?.usuarioLogadoDTO?.usuarioPerfilEmpresaDTO ||
    da?.usuarioLogadoDTO?.usuarioPerfilEmpresaDTO ||
    {};
  const listaPrivilegios = usuarioPerfil.listaPrivilegios || {};
  const tipoPerfilUsuario = usuarioPerfil.tipoPerfilUsuario || false;

  function verificarPermissao(permissao, ignorarPermissoes = false) {
    const nivel = listaPrivilegios?.[permissao];

    if (tipoPerfilUsuario === "MASTER" || ignorarPermissoes) {
      return {
        consultar: true,
        inserir: true,
        alterar: true,
        excluir: true,
        permissao: permissao,
        nivel: 4,
      };
    }

    switch (nivel) {
      case PERMISSIONS.CONSULTAR:
        return {
          consultar: true,
          inserir: false,
          alterar: false,
          excluir: false,
          permissao: permissao,
          nivel: 1,
        };
      case PERMISSIONS.INSERIR:
        return {
          consultar: true,
          inserir: true,
          alterar: false,
          excluir: false,
          permissao: permissao,
          nivel: 2,
        };
      case PERMISSIONS.ALTERAR:
        return {
          consultar: true,
          inserir: true,
          alterar: true,
          excluir: false,
          permissao: permissao,
          nivel: 3,
        };
      case PERMISSIONS.EXCLUIR:
        return {
          consultar: true,
          inserir: true,
          alterar: true,
          excluir: true,
          permissao: permissao,
          nivel: 4,
        };
      default:
        return {
          consultar: false,
          inserir: false,
          alterar: false,
          excluir: false,
          permissao: permissao,
          nivel: 0,
        };
    }
  }

  function verificarNivelNumerico(nivel) {
    let nivelNumerico = 4;

    if (nivel === PERMISSIONS.CONSULTAR) {
      nivelNumerico = 1;
    }
    if (nivel === PERMISSIONS.INSERIR) {
      nivelNumerico = 2;
    }
    if (nivel === PERMISSIONS.ALTERAR) {
      nivelNumerico = 3;
    }
    if (nivel === PERMISSIONS.EXCLUIR) {
      nivelNumerico = 4;
    }
    if (tipoPerfilUsuario === "MASTER") {
      nivelNumerico = 0;
    }
    return nivelNumerico;
  }

  function verificarConsultar(permissao, funcao = () => {}) {
    if (permissao.consultar) {
      funcao();
      return true;
    } else {
      toastError(
        `permissao-consultar-${permissao.permissao}`,
        `Você não tem permissão para realizar esta ação. É necessário a permissão ${permissao.permissao} com nível de permissão CONSULTAR.`,
      );
      return false;
    }
  }

  function verificarInserir(permissao, funcao = () => {}) {
    if (permissao.inserir) {
      funcao();
      return true;
    } else {
      toastError(
        `permissao-inserir-${permissao.permissao}`,
        `Você não tem permissão para realizar esta ação. É necessário a permissão ${permissao.permissao} com nível de permissão INSERIR.`,
      );
      return false;
    }
  }

  function verificarAlterar(permissao, funcao = () => {}) {
    if (permissao.alterar) {
      funcao();
      return true;
    } else {
      toastError(
        `permissao-alterar-${permissao.permissao}`,
        `Você não tem permissão para realizar esta ação. É necessário a permissão ${permissao.permissao} com nível de permissão ALTERAR.`,
      );
      return false;
    }
  }

  function verificarExcluir(permissao, funcao = () => {}) {
    if (permissao.excluir) {
      funcao();
      return true;
    } else {
      toastError(
        `permissao-excluir-${permissao.permissao}`,
        `Você não tem permissão para realizar esta ação. É necessário a permissão ${permissao.permissao} com nível de permissão EXCLUIR.`,
      );
      return false;
    }
  }

  return {
    verificarPermissao,
    verificarConsultar,
    verificarInserir,
    verificarAlterar,
    verificarExcluir,
    verificarNivelNumerico,
    PermissaoWrapper,
  };
}

export function PermissaoWrapper({
  children,
  permissoes,
  consultar = false,
  inserir = false,
  alterar = false,
  excluir = false,
  mensagemSemPermissao,
  style,
  ...rest
}) {
  const { verificarConsultar, verificarInserir, verificarAlterar, verificarExcluir } =
    usePermissoesLocalStorage();

  function validarPermissao() {
    if (consultar && !permissoes?.consultar) {
      if (mensagemSemPermissao) {
        toastError("permissao-wrapper-consultar", mensagemSemPermissao);
      } else {
        verificarConsultar(permissoes);
      }
      return false;
    }

    if (inserir && !permissoes?.inserir) {
      if (mensagemSemPermissao) {
        toastError("permissao-wrapper-inserir", mensagemSemPermissao);
      } else {
        verificarInserir(permissoes);
      }
      return false;
    }

    if (alterar && !permissoes?.alterar) {
      if (mensagemSemPermissao) {
        toastError("permissao-wrapper-alterar", mensagemSemPermissao);
      } else {
        verificarAlterar(permissoes);
      }
      return false;
    }

    if (excluir && !permissoes?.excluir) {
      if (mensagemSemPermissao) {
        toastError("permissao-wrapper-excluir", mensagemSemPermissao);
      } else {
        verificarExcluir(permissoes);
      }
      return false;
    }
    return true;
  }

  const interceptarClique = (event) => {
    if (validarPermissao()) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (typeof event.nativeEvent?.stopImmediatePropagation === "function") {
      event.nativeEvent.stopImmediatePropagation();
    }
  };

  return (
    <div {...rest} style={{ display: "contents", ...style }} onClickCapture={interceptarClique}>
      {children}
    </div>
  );
}
